import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AddComplaintType from './AddComplaintType';
import Table from '../common/Table';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { FaEdit, FaTrash } from 'react-icons/fa';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import RTPageLayout from '../common/RTPageLayout';

const ComplaintTypesList = ({ onBack }) => {
  const location = useLocation();
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editType, setEditType] = useState(null);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/complaints/types');
      setComplaintTypes(res.data);
    } catch (e) {
      setComplaintTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditType(null);
    setShowAddEdit(true);
  };

  const handleEdit = (type) => {
    setEditType(type);
    setShowAddEdit(true);
  };

  const handleDelete = async (type_id, type_name) => {
    const confirmed = await toastConfirm(
      true,
      `Delete complaint type "${type_name}"?`,
    );
    if (!confirmed) return;

    try {
      await apiClient.post(`/complaints/types/${type_id}/delete`);
      toastSuccess('Complaint type deleted successfully');
      fetchTypes();
    } catch (e) {
      console.error('Error deleting complaint type:', e);
      toastError('Failed to delete complaint type');
    }
  };

  if (showAddEdit) {
    return (
      <AddComplaintType
        type={editType}
        onClose={() => {
          setShowAddEdit(false);
          fetchTypes();
        }}
      />
    );
  }

  const columns = [
    { Header: 'Name', accessor: 'type_name' },
    { Header: 'Description', accessor: 'description' },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableExport: true,
      width: 100, // Make column smaller
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 20 }}>
          {' '}
          {/* Increased gap from 12 to 20 */}
          <FaEdit
            style={{ cursor: 'pointer', color: '#153C7F' }}
            onClick={() => handleEdit(row.original || row.values || row)}
            title="Edit"
          />
          <FaTrash
            style={{ cursor: 'pointer', color: '#d32f2f' }}
            onClick={() => {
              const typeId =
                (row.original && row.original.type_id) ||
                (row.values && row.values.type_id) ||
                row.type_id;
              const typeName =
                (row.original && row.original.type_name) ||
                (row.values && row.values.type_name) ||
                row.type_name;
              handleDelete(typeId, typeName);
            }}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Complaint Types"
      actions={
        <>
          <RTButton requiresWrite variant="primary" onClick={handleAdd}>
            Add Complaint Type
          </RTButton>
          <RTButton
            variant="secondary"
            onClick={onBack}
            style={{ marginLeft: 12 }}
          >
            Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <RTSpinner
            size="medium"
            showMessage={true}
            message="Loading complaint types..."
          />
        </div>
      ) : (
        <Table
          columns={columns}
          data={complaintTypes}
          loading={loading}
          exportable={false}
          showSearch={false}
          showPagination={true}
        />
      )}
    </RTPageLayout>
  );
};

export default ComplaintTypesList;
